"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    illusionHeading: string;
    illusionSub: string;
    thisPeriod: string;
    nextPeriod: string;
    fromNext: string;
    price: string;
    profit: string;
    perFormula: string;
    perLow: string;
    perHigh: string;
    looksCheap: string;
    wasNotCheap: string;
    illusionTitle: string;
    illusionDescription: string;
    perReasons: { label: string; detail: string }[];
    pbrReasons: { label: string; detail: string }[];
    reasonsHeading: string;
    perColumnTitle: string;
    perColumnSubtitle: string;
    pbrColumnTitle: string;
    pbrColumnSubtitle: string;
    reasonsFooter: string;
    reasonsTitle: string;
    reasonsDescription: string;
    steps: { title: string; question: string }[];
    flowHeading: string;
    flowSub: string;
    businessContent: string;
    relativeValuation: string;
    flowTitle: string;
    flowDescription: string;
  }
> = {
  ja: {
    illusionHeading: "一時的に利益が大きいと PER は低く見える",
    illusionSub: "利益が落ちれば PER は上がり、「割安」は消える",
    thisPeriod: "今期（利益が好調）",
    nextPeriod: "来期（利益が落ちる）",
    fromNext: "翌期以降",
    price: "株価",
    profit: "利益",
    perFormula: "PER = 株価 / 利益",
    perLow: "8 倍",
    perHigh: "20 倍",
    looksCheap: "「割安に見える」",
    wasNotCheap: "「割安ではなかった」",
    illusionTitle: "低い PER が割安を意味するとは限らない",
    illusionDescription:
      "今の利益が一時的に大きいだけなら、PER は低く見えます。利益が元に戻れば PER は跳ね上がり、見た目の割安感は消えます。",
    perReasons: [
      { label: "景気敏感", detail: "好況で利益が膨張" },
      { label: "一時的な特別利益", detail: "資産売却など" },
      { label: "成長鈍化の懸念", detail: "市場が先を織り込み" },
    ],
    pbrReasons: [
      { label: "資本効率が低い", detail: "ROE が低水準" },
      { label: "成長投資が不発", detail: "設備が利益に繋がらず" },
      { label: "業界全体の構造問題", detail: "需要減退など" },
    ],
    reasonsHeading: "PER と PBR では低い理由が違う",
    perColumnTitle: "PER が低いとき",
    perColumnSubtitle: "「利益は続くか？」を問う",
    pbrColumnTitle: "PBR が低いとき",
    pbrColumnSubtitle: "「資産を生かせているか？」を問う",
    reasonsFooter: "どちらも「低い = 割安」ではなく、低い理由を分けて確認することが大切",
    reasonsTitle: "PER と PBR の低さには、別々の理由がある",
    reasonsDescription:
      "PER は利益の持続性、PBR は資本効率の弱さが主な原因です。同じ「低い」でも見るべきポイントが違います。",
    steps: [
      { title: "収益源", question: "何で稼いでいるか" },
      { title: "利益の安定性", question: "続きそうか" },
      { title: "財務の健全さ", question: "無理はないか" },
      { title: "同業比較", question: "なぜこの会社だけ低いか" },
    ],
    flowHeading: "低い倍率を見たとき、この順番で確認する",
    flowSub: "「安い」と決めつける前に背景を整理するための 4 ステップ",
    businessContent: "事業の中身",
    relativeValuation: "相対的な評価",
    flowTitle: "倍率を見たときの確認フロー",
    flowDescription:
      "まず事業の中身を把握し、最後に同業との比較で「なぜこの会社だけ低いのか」を確認します。",
  },
  en: {
    illusionHeading: "When profit is temporarily large, P/E looks low",
    illusionSub: "If profit falls back, P/E rises and the “cheap” look disappears",
    thisPeriod: "This period (strong profit)",
    nextPeriod: "Next period (profit falls)",
    fromNext: "From next period",
    price: "Share price",
    profit: "Profit",
    perFormula: "P/E = price / profit",
    perLow: "8×",
    perHigh: "20×",
    looksCheap: "“Looks cheap”",
    wasNotCheap: "“It was not cheap”",
    illusionTitle: "A low P/E does not necessarily mean cheap",
    illusionDescription:
      "If current profit is only temporarily large, P/E looks low. When profit returns to normal, P/E jumps and the apparent cheapness disappears.",
    perReasons: [
      { label: "Cyclical", detail: "Profit inflated by a strong economy" },
      { label: "A one-off gain", detail: "An asset sale, for instance" },
      { label: "Concern about slowing growth", detail: "The market pricing ahead" },
    ],
    pbrReasons: [
      { label: "Low capital efficiency", detail: "ROE at a low level" },
      { label: "Growth investment misfiring", detail: "Capex not turning into profit" },
      { label: "Structural problems across the sector", detail: "Falling demand, for instance" },
    ],
    reasonsHeading: "P/E and P/B are low for different reasons",
    perColumnTitle: "When P/E is low",
    perColumnSubtitle: "Ask “will the profit continue?”",
    pbrColumnTitle: "When P/B is low",
    pbrColumnSubtitle: "Ask “are the assets being put to work?”",
    reasonsFooter: "For both, “low = cheap” does not hold — check the reason it is low",
    reasonsTitle: "A low P/E and a low P/B have separate causes",
    reasonsDescription:
      "P/E turns mainly on how durable the profit is; P/B on weak capital efficiency. Both are “low”, but you look at different things.",
    steps: [
      { title: "Source of earnings", question: "How does it make money?" },
      { title: "Stability of profit", question: "Is it likely to continue?" },
      { title: "Financial health", question: "Is anything stretched?" },
      { title: "Peer comparison", question: "Why is only this company low?" },
    ],
    flowHeading: "When you see a low multiple, check in this order",
    flowSub: "Four steps for laying out the background before concluding it is “cheap”",
    businessContent: "What the business is",
    relativeValuation: "Relative valuation",
    flowTitle: "A check flow for when you see a multiple",
    flowDescription:
      "Understand what the business is first, then use a peer comparison at the end to ask “why is only this company low?”.",
  },
};

// ── Colors ────────────────────────────────────────────────────────

const C = {
  profit: COLOR.sky,
  price: COLOR.emerald,
  warn: COLOR.rose,
  neutral: COLOR.amber,
  accent: "#7c3aed",
};

// ── Helpers ───────────────────────────────────────────────────────

function RoundedBar({
  x,
  y,
  w,
  h,
  color,
  opacity = 0.18,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  opacity?: number;
}) {
  return (
    <rect
      x={x}
      y={y}
      width={Math.max(w, 0)}
      height={h}
      rx={3}
      fill={color}
      fillOpacity={opacity}
      stroke={color}
      strokeWidth="1"
      strokeOpacity={1}
    />
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 1: Why low PER can be misleading
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function PERIllusionIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 230;
  const barW = 50;
  const baseY = 175;

  // Two scenarios side by side
  const leftCX = 120;
  const rightCX = 340;

  // Left: "looks cheap" — high current earnings, normal price
  const leftPriceH = 80;
  const leftEarningsH = 100;
  // Right: earnings drop, same price → PER rises
  const rightPriceH = 80;
  const rightEarningsH = 40;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={VW / 2} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.illusionHeading}
      </text>
      <text x={VW / 2} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.illusionSub}
      </text>

      {/* ── Left scenario ── */}
      <text x={leftCX} y={52} textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="600">
        {t.thisPeriod}
      </text>

      {/* Price bar */}
      <RoundedBar x={leftCX - barW - 8} y={baseY - leftPriceH} w={barW} h={leftPriceH} color={C.price} opacity={0.2} />
      <text
        x={leftCX - barW / 2 - 8}
        y={baseY - leftPriceH - 6}
        textAnchor="middle"
        fontSize="7.5"
        fill={C.price}
        fontWeight="600"
      >
        {t.price}
      </text>

      {/* Earnings bar */}
      <RoundedBar x={leftCX + 8} y={baseY - leftEarningsH} w={barW} h={leftEarningsH} color={C.profit} opacity={0.2} />
      <text
        x={leftCX + barW / 2 + 8}
        y={baseY - leftEarningsH - 6}
        textAnchor="middle"
        fontSize="7.5"
        fill={C.profit}
        fontWeight="600"
      >
        {t.profit}
      </text>

      {/* PER label */}
      <text x={leftCX} y={baseY + 16} textAnchor="middle" fontSize="8" fill={C.price} fontWeight="500">
        {t.perFormula}
      </text>
      <text x={leftCX} y={baseY + 30} textAnchor="middle" fontSize="12" fill={C.price} fontWeight="700">
        {t.perLow}
      </text>
      <text
        x={leftCX}
        y={baseY + 42}
        textAnchor="middle"
        fontSize="8"
        fill={C.price}
        fontWeight="500"
        fillOpacity={0.6}
      >
        {t.looksCheap}
      </text>

      {/* Arrow between scenarios */}
      <defs>
        <marker id="cmArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6" fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity={0.3} />
        </marker>
      </defs>
      <line
        x1={200}
        y1={110}
        x2={260}
        y2={110}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.2}
        markerEnd="url(#cmArrow)"
      />
      <text x={230} y={100} textAnchor="middle" fontSize="7.5" fill="currentColor" fillOpacity={0.45}>
        {t.fromNext}
      </text>

      {/* ── Right scenario ── */}
      <text x={rightCX} y={52} textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="600">
        {t.nextPeriod}
      </text>

      {/* Price bar (same) */}
      <RoundedBar
        x={rightCX - barW - 8}
        y={baseY - rightPriceH}
        w={barW}
        h={rightPriceH}
        color={C.price}
        opacity={0.2}
      />
      <text
        x={rightCX - barW / 2 - 8}
        y={baseY - rightPriceH - 6}
        textAnchor="middle"
        fontSize="7.5"
        fill={C.price}
        fontWeight="600"
      >
        {t.price}
      </text>

      {/* Earnings bar (shrunk) */}
      <RoundedBar x={rightCX + 8} y={baseY - rightEarningsH} w={barW} h={rightEarningsH} color={C.warn} opacity={0.2} />
      <text
        x={rightCX + barW / 2 + 8}
        y={baseY - rightEarningsH - 6}
        textAnchor="middle"
        fontSize="7.5"
        fill={C.warn}
        fontWeight="600"
      >
        {t.profit}
      </text>

      {/* Drop arrow on earnings */}
      <line
        x1={rightCX + barW / 2 + 8}
        y1={baseY - leftEarningsH + 5}
        x2={rightCX + barW / 2 + 8}
        y2={baseY - rightEarningsH - 12}
        stroke={C.warn}
        strokeWidth="1.2"
        strokeDasharray="3 3"
        opacity={0.5}
      />

      {/* PER label */}
      <text x={rightCX} y={baseY + 16} textAnchor="middle" fontSize="8" fill={C.warn} fontWeight="500">
        {t.perFormula}
      </text>
      <text x={rightCX} y={baseY + 30} textAnchor="middle" fontSize="12" fill={C.warn} fontWeight="700">
        {t.perHigh}
      </text>
      <text
        x={rightCX}
        y={baseY + 42}
        textAnchor="middle"
        fontSize="8"
        fill={C.warn}
        fontWeight="500"
        fillOpacity={0.6}
      >
        {t.wasNotCheap}
      </text>
    </svg>
  );
}

export function DocPERIllusionDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.illusionTitle}
      description={t.illusionDescription}
    >
      <PERIllusionIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 2: PER vs PBR — different reasons for being low
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type ReasonItem = { label: string; detail: string };

function ReasonColumn({
  title,
  subtitle,
  reasons,
  color,
  cx,
  startY,
}: {
  title: string;
  subtitle: string;
  reasons: ReasonItem[];
  color: string;
  cx: number;
  startY: number;
}) {
  const boxW = 170;
  const boxH = 30;
  const gap = 6;
  return (
    <g>
      <text x={cx} y={startY} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">
        {title}
      </text>
      <text x={cx} y={startY + 13} textAnchor="middle" fontSize="7.5" fill={color} fillOpacity={0.6}>
        {subtitle}
      </text>
      {reasons.map((r, i) => {
        const ry = startY + 22 + i * (boxH + gap);
        return (
          <g key={r.label}>
            <rect
              x={cx - boxW / 2}
              y={ry}
              width={boxW}
              height={boxH}
              rx={5}
              fill={color}
              fillOpacity={0.08}
              stroke={color}
              strokeWidth="0.8"
              strokeOpacity={0.4}
            />
            <text x={cx - boxW / 2 + 10} y={ry + 13} fontSize="8.5" fill={color} fontWeight="600">
              {r.label}
            </text>
            <text x={cx - boxW / 2 + 10} y={ry + 24} fontSize="7" fill={color} fillOpacity={0.6}>
              {r.detail}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function PERvsPBRIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 200;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={VW / 2} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.reasonsHeading}
      </text>

      {/* Divider */}
      <line
        x1={VW / 2}
        y1={36}
        x2={VW / 2}
        y2={VH - 8}
        stroke="currentColor"
        strokeWidth="0.5"
        strokeOpacity={0.12}
        strokeDasharray="4 4"
      />

      {/* Left: PER */}
      <ReasonColumn
        title={t.perColumnTitle}
        subtitle={t.perColumnSubtitle}
        reasons={t.perReasons}
        color={C.profit}
        cx={VW / 4}
        startY={42}
      />

      {/* Right: PBR */}
      <ReasonColumn
        title={t.pbrColumnTitle}
        subtitle={t.pbrColumnSubtitle}
        reasons={t.pbrReasons}
        color={C.neutral}
        cx={(VW * 3) / 4}
        startY={42}
      />

      {/* Bottom note */}
      <text x={VW / 2} y={VH - 4} textAnchor="middle" fontSize="8" fill="currentColor" fillOpacity={0.45}>
        {t.reasonsFooter}
      </text>
    </svg>
  );
}

export function DocPERvsPBRReasonsDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.reasonsTitle}
      description={t.reasonsDescription}
    >
      <PERvsPBRIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 3: 4-step verification flow
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type Step = { num: string };

const STEPS: Step[] = [
  { num: "1" },
  { num: "2" },
  { num: "3" },
  { num: "4" },
];

function VerificationFlowIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 170;
  const stepW = 90;
  const stepH = 68;
  const gap = 18;
  const totalW = STEPS.length * stepW + (STEPS.length - 1) * gap;
  const startX = (VW - totalW) / 2;
  const stepY = 46;

  const colors = [C.profit, C.price, C.neutral, C.accent];

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={VW / 2} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.flowHeading}
      </text>
      <text x={VW / 2} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.flowSub}
      </text>

      {/* Arrow marker */}
      <defs>
        <marker id="cmFlowArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6" fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity={0.3} />
        </marker>
      </defs>

      {STEPS.map((step, i) => {
        const sx = startX + i * (stepW + gap);
        const cx = sx + stepW / 2;
        const color = colors[i];

        return (
          <g key={step.num}>
            {/* Step box */}
            <rect
              x={sx}
              y={stepY}
              width={stepW}
              height={stepH}
              rx={8}
              fill={color}
              fillOpacity={0.07}
              stroke={color}
              strokeWidth="1"
              strokeOpacity={0.35}
            />

            {/* Step number circle */}
            <circle
              cx={cx}
              cy={stepY + 16}
              r={9}
              fill={color}
              fillOpacity={0.15}
              stroke={color}
              strokeWidth="1"
              strokeOpacity={0.5}
            />
            <text x={cx} y={stepY + 19.5} textAnchor="middle" fontSize="9" fill={color} fontWeight="700">
              {step.num}
            </text>

            {/* Step title */}
            <text x={cx} y={stepY + 38} textAnchor="middle" fontSize="8.5" fill={color} fontWeight="600">
              {t.steps[i]!.title}
            </text>

            {/* Step question */}
            <text x={cx} y={stepY + 52} textAnchor="middle" fontSize="7" fill={color} fillOpacity={0.65}>
              {t.steps[i]!.question}
            </text>

            {/* Arrow to next step */}
            {i < STEPS.length - 1 ? (
              <line
                x1={sx + stepW + 3}
                y1={stepY + stepH / 2}
                x2={sx + stepW + gap - 3}
                y2={stepY + stepH / 2}
                stroke="currentColor"
                strokeWidth="1"
                strokeOpacity={0.2}
                markerEnd="url(#cmFlowArrow)"
              />
            ) : null}
          </g>
        );
      })}

      {/* Bottom labels */}
      <text
        x={startX + stepW / 2}
        y={stepY + stepH + 18}
        textAnchor="middle"
        fontSize="7.5"
        fill={C.profit}
        fillOpacity={0.5}
      >
        {t.businessContent}
      </text>
      <text
        x={startX + totalW - stepW / 2}
        y={stepY + stepH + 18}
        textAnchor="middle"
        fontSize="7.5"
        fill={C.accent}
        fillOpacity={0.5}
      >
        {t.relativeValuation}
      </text>
      <line
        x1={startX + stepW}
        y1={stepY + stepH + 14}
        x2={startX + totalW - stepW}
        y2={stepY + stepH + 14}
        stroke="currentColor"
        strokeWidth="0.5"
        strokeOpacity={0.1}
      />
      <path
        d={`M ${startX + stepW / 2} ${stepY + stepH + 11} L ${startX + totalW - stepW / 2} ${stepY + stepH + 11}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
        strokeOpacity={0.08}
      />
    </svg>
  );
}

export function DocVerificationFlowDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.flowTitle}
      description={t.flowDescription}
    >
      <VerificationFlowIllustration />
    </IllustrationCard>
  );
}
